import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert
} from '@mui/material';
import { getApiUrl } from '../apiUrl';
import MapRadiusPicker from '../components/MapRadiusPicker';

const API_URL = getApiUrl();
const ADMIN_TOKEN_KEY = 'cs_admin_pin_token';

function adminHeaders() {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  return token ? { 'X-Admin-Pin-Token': token } : {};
}

export default function Admin() {
  const [pin, setPin] = useState('');
  const [authed, setAuthed] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  const [error, setError] = useState('');
  const [zones, setZones] = useState([]);
  const [mode, setMode] = useState('list'); // list | create
  const [name, setName] = useState('Branson Troop');
  const [zipCode, setZipCode] = useState('65616');
  const [center, setCenter] = useState(null); // [lng, lat]
  const [zoom, setZoom] = useState(10.5);
  const [radius, setRadius] = useState(10);
  const [busy, setBusy] = useState(false);

  const [populateStatus, setPopulateStatus] = useState('');
  const [populatingId, setPopulatingId] = useState(null);

  const loadZones = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/troop-zones`, { headers: adminHeaders() });
      setZones(res.data || []);
      setAuthed(true);
      setError('');
    } catch (err) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setAuthed(false);
      setError(err.response?.data?.error || 'Admin session expired');
    }
  };

  useEffect(() => {
    if (authed) loadZones();
  }, []);

  const handlePinLogin = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/admin/pin-login`, { pin });
      localStorage.setItem(ADMIN_TOKEN_KEY, res.data.token);
      setAuthed(true);
      await loadZones();
    } catch (err) {
      setError(err.response?.data?.error || 'PIN login failed');
    } finally {
      setBusy(false);
    }
  };

  const populateStores = async (id, troopName) => {
    if (!window.confirm(`Fetch Foursquare retail stores for "${troopName}"? Existing stores are kept; new ones are added.`)) {
      return;
    }
    setPopulatingId(id);
    setPopulateStatus('');
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/troop-zones/${id}/populate-stores`,
        {},
        { headers: adminHeaders(), timeout: 300000 }
      );
      const d = res.data;
      setPopulateStatus(
        `${troopName}: +${d.inserted} new, ${d.updated} updated (${d.storeCount} total)${d.source ? ` via ${d.source}` : ''}${d.note ? ` — ${d.note}` : ''}`
      );
      await loadZones();
    } catch (err) {
      setError(err.response?.data?.error || 'Populate stores failed');
    } finally {
      setPopulatingId(null);
    }
  };

  const startCreate = async () => {
    setMode('create');
    setError('');
    try {
      const geo = await axios.get(`${API_URL}/api/geocode/zip`, { params: { zip: zipCode } });
      setCenter(geo.data.coordinates);
      setZoom(geo.data.suggestedZoom || 10.5);
      setRadius(geo.data.suggestedRadius || 10);
    } catch (_err) {
      // Branson fallback
      setCenter([-93.2185, 36.6437]);
      setZoom(10.5);
      setRadius(10);
    }
  };

  const geocodeZip = async () => {
    setBusy(true);
    try {
      const geo = await axios.get(`${API_URL}/api/geocode/zip`, { params: { zip: zipCode } });
      setCenter(geo.data.coordinates);
      setZoom(geo.data.suggestedZoom || 10.5);
    } catch (err) {
      setError(err.response?.data?.error || 'Zip geocode failed');
    } finally {
      setBusy(false);
    }
  };

  const saveZone = async () => {
    if (!name.trim() || !center) {
      setError('Name and map center are required');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/admin/troop-zones`,
        {
          name: name.trim(),
          zipCode,
          centerLng: center[0],
          centerLat: center[1],
          radiusMiles: radius,
          mapZoom: zoom
        },
        { headers: adminHeaders() }
      );
      setMode('list');
      await loadZones();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save troop zone');
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this troop zone?')) return;
    try {
      await axios.put(
        `${API_URL}/api/admin/troop-zones/${id}`,
        { isActive: false },
        { headers: adminHeaders() }
      );
      await loadZones();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate');
    }
  };

  if (!authed) {
    return (
      <Container maxWidth="xs" sx={{ mt: 8 }}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>Admin Portal</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the admin backend PIN to continue.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              fullWidth
              type="password"
              label="Admin PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button fullWidth variant="contained" onClick={handlePinLogin} disabled={busy || !pin} sx={{ backgroundColor: '#202E40' }}>
              Unlock
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (mode === 'create' && center) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h5" gutterBottom>Add Troop Zone</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth
          label="Troop Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            label="Seed Zip Code"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
          />
          <Button variant="outlined" onClick={geocodeZip} disabled={busy || zipCode.length !== 5}>
            Geocode
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Press and hold to move the center, zoom to adjust radius
        </Typography>
        <MapRadiusPicker
          center={center}
          zoom={zoom}
          radius={radius}
          onCenterChange={setCenter}
          onZoomChange={setZoom}
          onRadiusChange={setRadius}
          onBack={() => setMode('list')}
          onContinue={saveZone}
          continueLabel="Save Troop"
          busy={busy}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Troop Zones</Typography>
        <Box>
          <Button
            sx={{ mr: 1 }}
            onClick={() => {
              localStorage.removeItem(ADMIN_TOKEN_KEY);
              setAuthed(false);
            }}
          >
            Lock
          </Button>
          <Button variant="contained" onClick={startCreate} sx={{ backgroundColor: '#202E40' }}>
            Add Troop
          </Button>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {populateStatus && <Alert severity="success" sx={{ mb: 2 }}>{populateStatus}</Alert>}
      <Card>
        <List>
          {zones.map((z, idx) => (
            <React.Fragment key={z.id}>
              {idx > 0 && <Divider />}
              <ListItem
                alignItems="flex-start"
                secondaryAction={
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={populatingId === z.id}
                      onClick={() => populateStores(z.id, z.name)}
                      sx={{ backgroundColor: '#202E40', whiteSpace: 'nowrap' }}
                    >
                      {populatingId === z.id ? 'Populating…' : 'Populate stores (Foursquare)'}
                    </Button>
                    {z.isActive ? (
                      <Button size="small" color="warning" onClick={() => deactivate(z.id)}>Deactivate</Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Inactive</Typography>
                    )}
                  </Box>
                }
              >
                <ListItemText
                  primary={`${z.name}${z.isActive ? '' : ' (inactive)'}`}
                  secondary={
                    <>
                      {`Zip ${z.zipCode || '—'} • ${z.radiusMiles} mi • center ${z.centerLat?.toFixed?.(3)}, ${z.centerLng?.toFixed?.(3)}`}
                      <br />
                      {z.lastStoresPopulatedAt
                        ? `Last populate: ${new Date(z.lastStoresPopulatedAt).toLocaleString()} (${z.lastStorePopulateCount || 0} stores)`
                        : 'Stores not populated yet'}
                    </>
                  }
                  sx={{ pr: 18 }}
                />
              </ListItem>
            </React.Fragment>
          ))}
          {zones.length === 0 && (
            <ListItem>
              <ListItemText primary="No troop zones yet. Add Branson Troop to get started." />
            </ListItem>
          )}
        </List>
      </Card>
    </Container>
  );
}
